import { Injectable } from '@angular/core';
import {
  deleteObject,
  getBytes,
  getDownloadURL,
  ref,
  uploadBytes,
} from '@angular/fire/storage';
import { TenantContextService } from './tenant-context.service';
import { TenantFirebaseService } from './tenant-firebase.service';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  constructor(
    private tenantFirebase: TenantFirebaseService,
    private tenantContext: TenantContextService,
  ) {}

  private resolvePath(path: string): string {
    const base = `tenants/${this.tenantContext.tenantId}`;
    return [base, path].join('/').replace(/\/\//g, '/');
  }

  async getDownloadURL(fileId: string): Promise<string> {
    // Handle paths that were mistakenly stored as full paths (with tenant prefix already included)
    const resolvedPath = fileId.startsWith('tenants/')
      ? fileId
      : this.resolvePath(fileId);
    const storageRef = ref(this.tenantFirebase.storage, resolvedPath);
    return getDownloadURL(storageRef);
  }

  async getBlob(fileId: string): Promise<Blob> {
    const storageRef = ref(
      this.tenantFirebase.storage,
      this.resolvePath(fileId),
    );
    const arrayBuffer = await getBytes(storageRef);
    const bytes = new Uint8Array(arrayBuffer);
    let mimeType = 'image/jpeg';
    if (bytes[0] === 0x89 && bytes[1] === 0x50) mimeType = 'image/png';
    else if (bytes[0] === 0xff && bytes[1] === 0xd8) mimeType = 'image/jpeg';
    else if (bytes[0] === 0x47 && bytes[1] === 0x49) mimeType = 'image/gif';
    return new Blob([arrayBuffer], { type: mimeType });
  }

  upload(path: string, data: Blob | Uint8Array | ArrayBuffer): Promise<string> {
    const storageRef = ref(this.tenantFirebase.storage, this.resolvePath(path));
    // Return the relative path (input), not fullPath, to avoid double-prefixing on read
    return uploadBytes(storageRef, data).then(() => path);
  }

  delete(path: string): Promise<void> {
    const storageRef = ref(this.tenantFirebase.storage, this.resolvePath(path));
    return deleteObject(storageRef);
  }
}
